import { useEffect, useMemo, useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
    Alert,
    Box,
    Checkbox,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import {
    activateMockTest,
    archiveMockTest,
    deleteMockTest,
    generateMockTest,
    getGenerationProfileList,
    getMockTest,
    getMockTestList,
    type GenerationProfileListItem,
    publishMockTestToTarget,
    republishMockTestToTarget,
    type MockTestPublishIssue,
    type MockTestTargetSyncResponse,
    type MockTestRecord,
    unpublishMockTest,
    updateMockTest,
    updateMockTestTitle,
    validatePublishMockTest,
} from '../admin/adminMockTestDataProvider';
import {
    EXAM_TASK_DESTINATION_RESOURCE,
    getExamTask,
    resolveExamTaskImageAssetUrl,
    updateExamTask,
} from '../admin/examTaskReviewDataProvider';
import { MediaPreview } from '../examTasks/MediaPreview';
import { examNames, examTaskCategories, getExamTaskTypeByApiType } from '../examTasks/examTaskTypes';
import { SegmentedTabs, type SegmentedTabOption } from '../admin/SegmentedTabs';
import { formatApiDateTime } from '../admin/dateTimeUtils';

type MockTestKind = '' | 'Sectional' | 'Full';
type PendingAction = 'activate' | 'archive' | 'delete' | 'publish' | 'republish' | 'unpublish' | 'validatePublish';

const publishActionButtonSx = {
    backgroundColor: '#7c3aed',
    borderColor: '#7c3aed',
    color: '#ffffff',
    '&:hover': {
        backgroundColor: '#6d28d9',
        borderColor: '#6d28d9',
    },
};

type FilterState = {
    examName: string;
    mockTestKind: string;
    examTaskCategory: string;
    status: string;
    publishedState: string;
    search: string;
};

type ActionState = {
    id: string;
    action: PendingAction;
    title: string;
};

type BulkAction = 'activate' | 'publish' | 'unpublish';

type BulkActionItem = {
    id: string;
    title: string;
};

type BulkActionState = {
    action: BulkAction;
    items: BulkActionItem[];
    skippedCount: number;
};

type TargetSyncFailureState = {
    actionLabel: string;
    title: string;
    errorMessage: string;
    issues: MockTestPublishIssue[];
};

type AutoPublishExamTaskItem = {
    examTaskId: string;
    examTaskType: string;
    questionNo: string | null;
};

type ResolvedIssueExamTask = {
    examTaskType: string | null;
    examName: string | null;
    questionNo: string | null;
};

type BulkActionResult = {
    id: string;
    title: string;
    success: boolean;
    errorMessage?: string | null;
    issues?: MockTestPublishIssue[] | null;
    rawResponse?: unknown | null;
};

type IsFreeValue = 'true' | 'false';

type GenerateDraft = {
    examName: string;
    generationProfileId: string;
    mockTestKind: string;
    sectionCategory: string;
    isFree: IsFreeValue;
};

const mockTestKinds: MockTestKind[] = ['', 'Sectional', 'Full'];

const mockTestKindLabels: Record<MockTestKind, string> = {
    '': 'All Kinds',
    Sectional: 'Sectional',
    Full: 'Full',
};

const accessTabs: SegmentedTabOption[] = [
    { value: 'false', label: 'Paid' },
    { value: 'true', label: 'Free' },
];

const targetPublishStateTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'published', label: 'Published' },
    { value: 'unpublished', label: 'Unpublished' },
];

function formatDateTime(value: unknown): string {
    return formatApiDateTime(value);
}

function formatValue(value: unknown, fallback = 'N/A'): string {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value);
}

function getFirstString(item: MockTestRecord, keys: string[]): string | null {
    for (const key of keys) {
        const value = item[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return null;
}

function getFirstNumber(item: MockTestRecord, keys: string[]): number | null {
    for (const key of keys) {
        const value = item[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
    }

    return null;
}

function getFirstBoolean(item: MockTestRecord, keys: string[]): boolean | null {
    for (const key of keys) {
        const value = item[key];
        if (typeof value === 'boolean') {
            return value;
        }
    }

    return null;
}

function getIsFreeValue(item: MockTestRecord): boolean {
    return getFirstBoolean(item, ['isFree', 'free', 'is_free']) ?? false;
}

function getIsPublishedToTarget(item: MockTestRecord): boolean {
    return getFirstBoolean(item, ['isPublishedToTarget']) ?? false;
}

function toIsFreeValue(value: boolean): IsFreeValue {
    return value ? 'true' : 'false';
}

function formatAccessLabel(value: boolean): string {
    return value ? 'Free' : 'Paid';
}

function toTitleCase(value: string): string {
    return value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[-_]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}

function buildGeneratedTitle(item: MockTestRecord): string {
    const examName = getFirstString(item, ['examName']) ?? 'Mock Test';
    const kind = getFirstString(item, ['mockTestKind']) ?? '';
    const category = getFirstString(item, ['examTaskCategory', 'sectionCategory']) ?? '';
    const version = getFirstNumber(item, ['version', 'revision', 'sequenceNo', 'sortOrder']);

    const baseTitle = [examName, kind, category, 'Mock Test']
        .filter(Boolean)
        .map((segment) => toTitleCase(segment))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (version === null) {
        return baseTitle || 'Mock Test';
    }

    return `${baseTitle || 'Mock Test'} #${version}`;
}

function getMockTestTitle(item: MockTestRecord): string {
    const candidates = [item.title, item.name, item.mockTestTitle, item.mockTestName, item.displayTitle];
    const selected = candidates.find((value) => typeof value === 'string' && value.trim());
    if (typeof selected === 'string') {
        return selected;
    }

    return buildGeneratedTitle(item);
}

function getMockTestStatus(item: MockTestRecord): string {
    const candidates = [item.status, item.publishStatus, item.lifecycleStatus, item.state];
    const selected = candidates.find((value) => typeof value === 'string' && value.trim());
    return typeof selected === 'string' ? selected : 'Unknown';
}

function matchesSearch(item: MockTestRecord, query: string): boolean {
    if (!query.trim()) return true;

    const haystack = [
        getMockTestTitle(item),
        getMockTestSubtitle(item),
        getMockTestStatus(item),
        formatValue(item.examName, ''),
        formatValue(item.mockTestKind, ''),
        formatValue(item.examTaskCategory, ''),
    ]
        .join(' ')
        .toLowerCase();

    return haystack.includes(query.trim().toLowerCase());
}

function matchesStatus(item: MockTestRecord, status: string): boolean {
    if (!status) return true;
    return getMockTestStatus(item).toLowerCase() === status.toLowerCase();
}

function getMockTestSubtitle(item: MockTestRecord): string {
    const code = getFirstString(item, ['code', 'mockTestCode', 'slug']);
    const id = getFirstString(item, ['id']);
    return code ?? id ?? 'N/A';
}

function getSummaryMetrics(item: MockTestRecord) {
    const resolvedSections = getSectionRecords(item);
    const durationMinutes = getFirstNumber(item, ['durationMinutes', 'totalDurationMinutes', 'durationInMinutes']);
    const durationSeconds = getFirstNumber(item, ['totalDurationSeconds', 'durationSeconds', 'totalDurationInSeconds']);

    return [
        {
            label: 'Exam Tasks',
            value: getFirstNumber(item, ['examTaskCount', 'taskCount', 'totalExamTasks', 'questionCount']),
        },
        {
            label: 'Sections',
            value: resolvedSections.length || getFirstNumber(item, ['sectionCount', 'totalSections']),
        },
        {
            label: 'Duration',
            value: durationMinutes ?? (durationSeconds !== null ? Math.round(durationSeconds / 60) : null),
            suffix: 'min',
        },
    ].filter((entry) => entry.value !== null);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getFirstArray(item: MockTestRecord | Record<string, unknown>, keys: string[]): Record<string, unknown>[] {
    for (const key of keys) {
        const value = item[key];
        if (Array.isArray(value)) {
            return value.filter(isRecord);
        }
    }

    return [];
}

function getSectionRecords(item: MockTestRecord): Record<string, unknown>[] {
    return getFirstArray(item, ['sections', 'mockTestSections', 'sectionDtos']);
}

function getExamTaskRecord(item: Record<string, unknown>): Record<string, unknown> | null {
    const value = item.examTask;
    return isRecord(value) ? value : null;
}

function getItemGroupRecords(item: Record<string, unknown>): Record<string, unknown>[] {
    return getFirstArray(item, ['itemGroups', 'groups', 'itemGroupDtos']);
}

function getItemRecords(item: Record<string, unknown>): Record<string, unknown>[] {
    return getFirstArray(item, ['items', 'examTasks', 'examTaskItems', 'questions']);
}

function getSegmentRecords(item: Record<string, unknown>): Record<string, unknown>[] {
    const nestedExamTask = getExamTaskRecord(item);
    return getFirstArray(item, ['segments', 'examTaskSegments', 'itemSegments']).length > 0
        ? getFirstArray(item, ['segments', 'examTaskSegments', 'itemSegments'])
        : nestedExamTask
          ? getFirstArray(nestedExamTask, ['segments', 'examTaskSegments', 'itemSegments'])
          : [];
}

function getVoiceRecords(item: Record<string, unknown>): Record<string, unknown>[] {
    const nestedExamTask = getExamTaskRecord(item);
    return getFirstArray(item, ['voices', 'voiceTracks', 'voiceDtos']).length > 0
        ? getFirstArray(item, ['voices', 'voiceTracks', 'voiceDtos'])
        : nestedExamTask
          ? getFirstArray(nestedExamTask, ['voices', 'voiceTracks', 'voiceDtos'])
          : [];
}

function formatColumnLabel(key: string): string {
    return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (char) => char.toUpperCase());
}

function formatDisplayValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return JSON.stringify(value);
}

function hasContentValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
}

function getPrimitiveEntries(item: MockTestRecord): Array<{ key: string; value: unknown }> {
    return Object.entries(item)
        .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value) && hasContentValue(value))
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => ({ key, value }));
}

function getStructureMetrics(item: MockTestRecord) {
    const sections = getSectionRecords(item);
    const itemGroups = sections.flatMap((section) => getItemGroupRecords(section));
    const sectionItems = sections.flatMap((section) => getItemRecords(section));
    const groupedItems = itemGroups.flatMap((group) => getItemRecords(group));
    const items = [...sectionItems, ...groupedItems];
    const segments = items.flatMap((entry) => getSegmentRecords(entry));
    const voices = items.flatMap((entry) => getVoiceRecords(entry));

    return [
        { label: 'Sections', value: sections.length },
        { label: 'Item Groups', value: itemGroups.length },
        { label: 'Items', value: items.length },
        { label: 'Segments', value: segments.length },
        { label: 'Voices', value: voices.length },
    ];
}

function getNestedRecordTitle(item: Record<string, unknown>, fallback: string): string {
    const nestedExamTask = getExamTaskRecord(item);
    const value =
        getFirstString(item as MockTestRecord, ['title', 'name', 'displayTitle', 'mockTestTitle', 'sectionTitle']) ??
        (nestedExamTask
            ? getFirstString(nestedExamTask as MockTestRecord, ['displayTitle', 'title', 'name'])
            : null) ??
        getFirstString(item as MockTestRecord, ['code', 'slug', 'id']);

    return value ?? fallback;
}

function getSectionNavigationTitle(section: Record<string, unknown>, fallback: string): string {
    return getFirstString(section as MockTestRecord, ['slug']) ?? fallback;
}

function getItemType(item: Record<string, unknown>): string | null {
    const nestedExamTask = getExamTaskRecord(item);

    return (
        getFirstString(item as MockTestRecord, ['examTaskType', 'itemType', 'type']) ??
        (nestedExamTask ? getFirstString(nestedExamTask as MockTestRecord, ['examTaskType', 'itemType', 'type']) : null)
    );
}

function stripExamTaskTypePrefix(title: string, itemType: string | null): string {
    if (!itemType) {
        return title;
    }

    const escapedType = itemType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const readableType = toTitleCase(itemType).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    return title
        .replace(new RegExp(`^${escapedType}\\s*[-:]\\s*`, 'i'), '')
        .replace(new RegExp(`^${readableType}\\s*[-:]\\s*`, 'i'), '')
        .trim();
}

function getDisplayItemTitle(item: Record<string, unknown>, fallback: string): string {
    const title = getNestedRecordTitle(item, fallback);
    return stripExamTaskTypePrefix(title, getItemType(item));
}

function getGroupTitle(item: Record<string, unknown>, fallback: string): string {
    return getItemType(item) ?? getNestedRecordTitle(item, fallback);
}

function getGroupItemCount(item: Record<string, unknown>, items: Record<string, unknown>[]): number {
    return getFirstNumber(item as MockTestRecord, ['plannedItemCount', 'itemCount', 'totalItems']) ?? items.length;
}

function getExamTaskField(item: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
        if (key in item) {
            const value = item[key];
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }
    }

    const nestedExamTask = getExamTaskRecord(item);
    if (!nestedExamTask) {
        return undefined;
    }

    for (const key of keys) {
        if (key in nestedExamTask) {
            const value = nestedExamTask[key];
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }
    }

    return undefined;
}

function getExamTaskText(item: Record<string, unknown>, keys: string[]): string | null {
    const value = getExamTaskField(item, keys);
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }

    if (Array.isArray(value)) {
        const parts = value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
        return parts.length > 0 ? parts.join(', ') : null;
    }

    return null;
}

function getMediaUrl(item: Record<string, unknown>): string | null {
    const mediaUrl = getExamTaskText(item, ['mediaResourceUrl', 'mediaUrl', 'imageUrl']);
    if (!mediaUrl || getItemType(item) !== 'DescribeImage') {
        return mediaUrl;
    }

    return resolveExamTaskImageAssetUrl(mediaUrl);
}

function getDiscoveryTags(item: Record<string, unknown>): string[] {
    const value = getExamTaskField(item, ['discoveryTags']);

    if (typeof value === 'string') {
        return value
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean);
    }

    if (Array.isArray(value)) {
        return value
            .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
            .map((entry) => entry.trim());
    }

    return [];
}

function getAudioScripts(item: Record<string, unknown>): string[] {
    const directScript = getExamTaskText(item, ['audioScript', 'voiceScript', 'transcript', 'script']);
    const voiceScripts = getVoiceRecords(item)
        .map((voice) => getFirstString(voice as MockTestRecord, ['script', 'transcript', 'text', 'content', 'voiceScript']))
        .filter((entry): entry is string => Boolean(entry));

    return Array.from(new Set([directScript, ...voiceScripts].filter((entry): entry is string => Boolean(entry))));
}

function getVoiceMediaUrls(item: Record<string, unknown>): string[] {
    return Array.from(
        new Set(
            getVoiceRecords(item)
                .map((voice) => getFirstString(voice as MockTestRecord, ['mediaResourceUrl', 'audioUrl', 'voiceUrl', 'url']))
                .filter((entry): entry is string => Boolean(entry)),
        ),
    );
}

function formatSegmentText(segment: Record<string, unknown>): string {
    const prefix = getFirstString(segment as MockTestRecord, ['prefixText', 'text', 'promptText']) ?? '';
    const suffix = getFirstString(segment as MockTestRecord, ['suffixText']) ?? '';
    const answerKey = getFirstString(segment as MockTestRecord, ['answerKey']) ?? '';
    const optionList = Array.isArray(segment.optionList)
        ? segment.optionList
              .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
              .join(', ')
        : '';

    return [prefix, suffix, answerKey ? `Answer: ${answerKey}` : '', optionList ? `Options: ${optionList}` : '']
        .filter(Boolean)
        .join(' ')
        .trim();
}

function getSectionAnchorId(index: number): string {
    return `mock-test-section-${index}`;
}

function getGroupAnchorId(sectionIndex: number, groupIndex: number): string {
    return `mock-test-section-${sectionIndex}-group-${groupIndex}`;
}

function getDirectItemsAnchorId(sectionIndex: number): string {
    return `mock-test-section-${sectionIndex}-direct-items`;
}

function scrollToAnchor(anchorId: string) {
    const element = document.getElementById(anchorId);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

function buildMockTestDetailUrl(mockTestId: string): string {
    const url = new URL(window.location.href);
    url.hash = `#/mock-tests/${mockTestId}`;
    return url.toString();
}

type MockTestItemPagerProps = {
    anchorId: string;
    title: string;
    subtitle: string;
    items: Record<string, unknown>[];
    emptyMessage: string;
};

function MockTestItemDetail({ item }: { item: Record<string, unknown> }) {
    const promptBody = getExamTaskText(item, ['promptBody', 'promptText', 'prompt', 'text']);
    const discoveryTags = getDiscoveryTags(item);
    const audioScripts = getAudioScripts(item);
    const segments = getSegmentRecords(item);
    const mediaUrl = getMediaUrl(item);
    const voiceMediaUrls = getVoiceMediaUrls(item);

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h6">{getDisplayItemTitle(item, 'Item')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {[getItemType(item), getExamTaskText(item, ['complexityRank'])].filter(Boolean).join(' • ') || 'Exam task detail'}
                </Typography>
            </Box>

            {promptBody ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Prompt Body
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {promptBody}
                        </Typography>
                    </CardContent>
                </Card>
            ) : null}

            {mediaUrl || voiceMediaUrls.length > 0 ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Media
                        </Typography>
                        <Stack spacing={1.5}>
                            {mediaUrl ? <MediaPreview url={mediaUrl} title={getDisplayItemTitle(item, 'Media')} showUrl /> : null}
                            {voiceMediaUrls.map((url) => (
                                <MediaPreview key={url} url={url} title="Voice track" showUrl showWaveform />
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {discoveryTags.length > 0 ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Discovery Tags
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {discoveryTags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" />
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {audioScripts.length > 0 ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Audio Script
                        </Typography>
                        <Stack spacing={1.5}>
                            {audioScripts.map((script, index) => (
                                <Typography key={`${script}-${index}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {script}
                                </Typography>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {segments.some((segment) => formatSegmentText(segment)) ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Segments
                        </Typography>
                        <Stack spacing={1.5}>
                            {segments.map((segment, index) => {
                                const text = formatSegmentText(segment);
                                if (!text) {
                                    return null;
                                }

                                return (
                                    <Box key={String(segment.id ?? segment.examTaskSegmentId ?? index)} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Segment {getFirstNumber(segment as MockTestRecord, ['sequenceNumber']) ?? index + 1}
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {text}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}
        </Stack>
    );
}

function MockTestItemPager({ anchorId, title, subtitle, items, emptyMessage }: MockTestItemPagerProps) {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        setSelectedIndex(0);
    }, [items.length]);

    if (items.length === 0) {
        return (
            <Box id={anchorId} sx={{ scrollMarginTop: 96 }}>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2">{title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {emptyMessage}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const currentItem = items[selectedIndex] ?? items[0];

    return (
        <Box id={anchorId} sx={{ scrollMarginTop: 96 }}>
            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: 'background.paper',
                                py: 0.5,
                            }}
                        >
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="subtitle1">{title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {subtitle}
                                    </Typography>
                                </Box>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={selectedIndex === 0}
                                            onClick={() => setSelectedIndex((current) => Math.max(current - 1, 0))}
                                        >
                                            Prev
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={selectedIndex >= items.length - 1}
                                            onClick={() => setSelectedIndex((current) => Math.min(current + 1, items.length - 1))}
                                        >
                                            Next
                                        </Button>
                                    </Stack>
                                    <Chip label={`Item ${selectedIndex + 1} of ${items.length}`} size="small" color="primary" variant="outlined" />
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {items.map((entry, index) => (
                                        <Button
                                            key={String(entry.id ?? entry.mockTestItemId ?? index)}
                                            variant={index === selectedIndex ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => setSelectedIndex(index)}
                                        >
                                            {getDisplayItemTitle(entry, `Item ${index + 1}`)}
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                        </Box>

                        <MockTestItemDetail item={currentItem} />
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}

function MockTestDetailContent({
    item,
    isFreeUpdating,
    onIsFreeChange,
}: {
    item: MockTestRecord;
    isFreeUpdating: boolean;
    onIsFreeChange: (value: IsFreeValue) => void;
}) {
    const summaryMetrics = getSummaryMetrics(item);
    const structureMetrics = getStructureMetrics(item);
    const sections = getSectionRecords(item);
    const primitiveEntries = getPrimitiveEntries(item);
    const isFreeValue = toIsFreeValue(getIsFreeValue(item));
    const navigationSections = useMemo(() => {
        return sections.map((section, sectionIndex) => {
            const itemGroups = getItemGroupRecords(section);
            const directItems = getItemRecords(section);

            return {
                anchorId: getSectionAnchorId(sectionIndex),
                title: getSectionNavigationTitle(section, `Section ${sectionIndex + 1}`),
                subtitle: [
                    getFirstString(section as MockTestRecord, ['examTaskCategory', 'sectionCategory', 'category']),
                    getFirstNumber(section as MockTestRecord, ['durationSeconds'])
                        ? `${Math.round((getFirstNumber(section as MockTestRecord, ['durationSeconds']) ?? 0) / 60)} min`
                        : null,
                ]
                    .filter(Boolean)
                    .join(' • '),
                groups: itemGroups.map((group, groupIndex) => ({
                    anchorId: getGroupAnchorId(sectionIndex, groupIndex),
                    title: getGroupTitle(group, `Item Group ${groupIndex + 1}`),
                    itemCount: getGroupItemCount(group, getItemRecords(group)),
                })),
                directItemsAnchorId: directItems.length > 0 ? getDirectItemsAnchorId(sectionIndex) : null,
                directItemsCount: directItems.length,
            };
        });
    }, [sections]);

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h6">{getMockTestTitle(item)}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {getMockTestSubtitle(item)}
                </Typography>
            </Box>

            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={1.5}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                            <Box>
                                <Typography variant="subtitle1">Mock Test Access</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Admins can change whether this mock test is free or paid at any time.
                                </Typography>
                            </Box>
                            <Chip
                                label={formatAccessLabel(isFreeValue === 'true')}
                                color={isFreeValue === 'true' ? 'success' : 'default'}
                                variant="outlined"
                                size="small"
                            />
                        </Stack>
                        <SegmentedTabs
                            label="Access"
                            value={isFreeValue}
                            options={accessTabs}
                            onChange={(nextValue) => onIsFreeChange(nextValue as IsFreeValue)}
                            disabled={isFreeUpdating}
                            helperText={isFreeUpdating ? 'Updating access...' : 'Free mock tests are available without paid access.'}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                        xs: '1fr',
                        lg: '280px minmax(0, 1fr)',
                    },
                    alignItems: 'start',
                }}
            >
                <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: { lg: 0 } }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                Quick Navigation
                            </Typography>
                            <Stack
                                spacing={1}
                                sx={{
                                    maxHeight: {
                                        xs: 320,
                                        lg: 'calc(100vh - 180px)',
                                    },
                                    overflowY: 'auto',
                                    pr: 1,
                                    pb: '100px',
                                }}
                            >
                                {navigationSections.map((section) => (
                                    <Box key={section.anchorId}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => scrollToAnchor(section.anchorId)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                fontSize: '1.15rem',
                                                fontWeight: 700,
                                                py: 1.25,
                                                textTransform: 'none',
                                            }}
                                        >
                                            {section.title}
                                        </Button>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 0.75, fontSize: '0.95rem' }}
                                        >
                                            {section.subtitle || 'Section'}
                                        </Typography>
                                        <Stack spacing={1} sx={{ mt: 1.25, ml: 1 }}>
                                            {section.groups.map((group) => (
                                                <Button
                                                    key={group.anchorId}
                                                    fullWidth
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => scrollToAnchor(group.anchorId)}
                                                    sx={{
                                                        justifyContent: 'space-between',
                                                        fontSize: '1rem',
                                                        py: 0.75,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    <span>{group.title}</span>
                                                    <span>{group.itemCount}</span>
                                                </Button>
                                            ))}
                                            {section.directItemsAnchorId ? (
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => scrollToAnchor(section.directItemsAnchorId ?? '')}
                                                    sx={{
                                                        justifyContent: 'space-between',
                                                        fontSize: '1rem',
                                                        py: 0.75,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    <span>Direct Items</span>
                                                    <span>{section.directItemsCount}</span>
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                lg: '1fr',
                            },
                        }}
                    >
                        {[...summaryMetrics, ...structureMetrics].map((metric) => (
                            <Card key={metric.label} variant="outlined">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">
                                        {metric.label}
                                    </Typography>
                                    <Typography variant="h6">
                                        {'suffix' in metric && metric.suffix ? `${metric.value} ${metric.suffix}` : metric.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    {primitiveEntries.length > 0 ? (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                    Mock Test Fields
                                </Typography>
                                <Stack spacing={1.5}>
                                    {primitiveEntries.map((entry) => (
                                        <Box key={entry.key}>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatColumnLabel(entry.key)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {entry.key.toLowerCase().endsWith('at')
                                                    ? formatDateTime(entry.value)
                                                    : formatDisplayValue(entry.value)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ) : null}
                </Stack>

                <Stack spacing={3}>
                    {sections.length === 0 ? (
                        <Alert severity="info">No section details were returned for this mock test.</Alert>
                    ) : (
                        sections.map((section, index) => {
                            const itemGroups = getItemGroupRecords(section);
                            const directItems = getItemRecords(section);
                            const sectionDurationSeconds = getFirstNumber(section as MockTestRecord, ['durationSeconds']);

                            return (
                                <Card key={String(section.id ?? index)} id={getSectionAnchorId(index)} variant="outlined" sx={{ scrollMarginTop: 96 }}>
                                    <CardContent>
                                        <Stack spacing={2.5}>
                                            <Box>
                                                <Typography variant="h6">
                                                    {getNestedRecordTitle(section, `Section ${index + 1}`)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {[
                                                        getFirstString(section as MockTestRecord, ['examTaskCategory', 'sectionCategory', 'category']),
                                                        sectionDurationSeconds !== null ? `${Math.round(sectionDurationSeconds / 60)} min` : '',
                                                        getFirstString(section as MockTestRecord, ['status', 'publishStatus']),
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' • ') || 'No additional section metadata'}
                                                </Typography>
                                            </Box>

                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                <Chip label={`${itemGroups.length} item groups`} size="small" variant="outlined" />
                                                <Chip label={`${directItems.length} direct items`} size="small" variant="outlined" />
                                            </Stack>

                                            {itemGroups.map((group, groupIndex) => {
                                                const groupItems = getItemRecords(group);
                                                const groupItemCount = getGroupItemCount(group, groupItems);

                                                return (
                                                    <MockTestItemPager
                                                        key={String(group.id ?? `${index}-${groupIndex}`)}
                                                        anchorId={getGroupAnchorId(index, groupIndex)}
                                                        title={getGroupTitle(group, `Item Group ${groupIndex + 1}`)}
                                                        subtitle={[
                                                            `${groupItemCount} items`,
                                                            getFirstNumber(group as MockTestRecord, ['perItemHardTimeLimitSeconds'])
                                                                ? `${getFirstNumber(group as MockTestRecord, ['perItemHardTimeLimitSeconds'])} sec each`
                                                                : '',
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' • ')}
                                                        items={groupItems}
                                                        emptyMessage="No exam tasks were returned for this item group."
                                                    />
                                                );
                                            })}

                                            {directItems.length > 0 ? (
                                                <MockTestItemPager
                                                    anchorId={getDirectItemsAnchorId(index)}
                                                    title="Direct Items"
                                                    subtitle={`${directItems.length} items`}
                                                    items={directItems}
                                                    emptyMessage="No direct items were returned for this section."
                                                />
                                            ) : null}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </Stack>
            </Box>
        </Stack>
    );
}

export function MockTestDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const notify = useNotify();
    const [mockTestDetail, setMockTestDetail] = useState<MockTestRecord | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [isFreeUpdating, setIsFreeUpdating] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [titleSaving, setTitleSaving] = useState(false);

    useEffect(() => {
        if (!id) {
            setMockTestDetail(null);
            setDetailError('Mock test id is missing.');
            setDetailLoading(false);
            return;
        }

        let active = true;

        const load = async () => {
            setDetailLoading(true);
            setDetailError(null);
            try {
                const response = await getMockTest(id);
                if (!active) return;
                setMockTestDetail(response.data);
            } catch (error) {
                if (!active) return;
                setMockTestDetail(null);
                const message = error instanceof Error ? error.message : 'Failed to load mock test detail';
                setDetailError(message);
                notify(message, { type: 'error' });
            } finally {
                if (active) {
                    setDetailLoading(false);
                }
            }
        };

        void load();

        return () => {
            active = false;
        };
    }, [id, notify]);

    useEffect(() => {
        if (mockTestDetail) {
            setTitleDraft(getMockTestTitle(mockTestDetail));
        } else {
            setTitleDraft('');
        }
    }, [mockTestDetail]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/mock-tests');
    };

    const handleIsFreeChange = async (nextValue: IsFreeValue) => {
        if (!id || !mockTestDetail) {
            return;
        }

        const nextIsFree = nextValue === 'true';
        if (getIsFreeValue(mockTestDetail) === nextIsFree) {
            return;
        }

        setIsFreeUpdating(true);
        try {
            await updateMockTest(id, { isFree: nextIsFree });
            setMockTestDetail((current) => (current ? { ...current, isFree: nextIsFree } : current));
            notify(`Mock test marked as ${formatAccessLabel(nextIsFree).toLowerCase()}.`, { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to update mock test access', { type: 'error' });
        } finally {
            setIsFreeUpdating(false);
        }
    };

    const handleSaveTitle = async () => {
        if (!id || !mockTestDetail) return;
        const newTitle = titleDraft.trim();
        if (!newTitle) {
            notify('Title cannot be empty', { type: 'warning' });
            return;
        }

        const currentTitle = getMockTestTitle(mockTestDetail);
        if (currentTitle === newTitle) {
            setIsEditingTitle(false);
            return;
        }

        setTitleSaving(true);
        try {
            await updateMockTestTitle(id, newTitle);
            setMockTestDetail((current) => (current ? { ...current, title: newTitle } : current));
            setIsEditingTitle(false);
            notify('Mock test title updated', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to update mock test title', { type: 'error' });
        } finally {
            setTitleSaving(false);
        }
    };

    return (
        <Card sx={{ width: '100%' }}>
            <Title title={mockTestDetail ? getMockTestTitle(mockTestDetail) : 'Mock Test Detail'} />
            <CardContent>
                <Stack spacing={3}>
                    <Box>
                        <Button
                            variant="text"
                            startIcon={<ArrowBackOutlinedIcon />}
                            onClick={handleBack}
                            sx={{ mb: 1, pl: 0, textTransform: 'none' }}
                        >
                            Back to Mock Tests
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {mockTestDetail ? (
                                isEditingTitle ? (
                                    <>
                                        <TextField
                                            value={titleDraft}
                                            onChange={(e) => setTitleDraft(e.target.value)}
                                            size="small"
                                            sx={{ minWidth: 360 }}
                                            disabled={titleSaving}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveOutlinedIcon />}
                                            onClick={handleSaveTitle}
                                            disabled={titleSaving || titleDraft.trim() === ''}
                                        >
                                            {titleSaving ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button
                                            variant="text"
                                            startIcon={<CancelOutlinedIcon />}
                                            onClick={() => {
                                                setIsEditingTitle(false);
                                                setTitleDraft(getMockTestTitle(mockTestDetail));
                                            }}
                                            disabled={titleSaving}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="h4" fontWeight={700} sx={{ mr: 1 }}>
                                            {getMockTestTitle(mockTestDetail)}
                                        </Typography>
                                        <Button
                                            variant="text"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={() => setIsEditingTitle(true)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Edit
                                        </Button>
                                    </>
                                )
                            ) : (
                                <Typography variant="h4" fontWeight={700}>Mock Test Detail</Typography>
                            )}
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            {mockTestDetail
                                ? getMockTestSubtitle(mockTestDetail)
                                : 'Review the full mock test structure and exam task details.'}
                        </Typography>
                    </Box>

                    {detailLoading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading mock test detail...
                            </Typography>
                        </Stack>
                    ) : detailError ? (
                        <Alert severity="error">{detailError}</Alert>
                    ) : mockTestDetail ? (
                        <MockTestDetailContent
                            item={mockTestDetail}
                            isFreeUpdating={isFreeUpdating}
                            onIsFreeChange={(nextValue) => {
                                void handleIsFreeChange(nextValue);
                            }}
                        />
                    ) : (
                        <Alert severity="info">No mock test detail is available.</Alert>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function emptyGenerateDraft(): GenerateDraft {
    return {
        examName: 'Core',
        generationProfileId: '',
        mockTestKind: 'Sectional',
        sectionCategory: 'Speaking',
        isFree: 'false',
    };
}

function getActionLabel(action: PendingAction): string {
    if (action === 'activate') return 'Activate';
    if (action === 'archive') return 'Archive';
    if (action === 'delete') return 'Delete';
    if (action === 'publish') return 'Publish';
    if (action === 'republish') return 'Republish';
    if (action === 'validatePublish') return 'Validate Publish';
    return 'Unpublish';
}

function getActionVerb(action: PendingAction): string {
    if (action === 'activate') return 'activate';
    if (action === 'archive') return 'archive';
    if (action === 'delete') return 'delete';
    if (action === 'publish') return 'publish to the target environment';
    if (action === 'republish') return 'republish';
    if (action === 'validatePublish') return 'validate the target publish state for';
    return 'unpublish';
}

function getActionPastTense(action: PendingAction): string {
    if (action === 'activate') return 'activated';
    if (action === 'archive') return 'archived';
    if (action === 'delete') return 'deleted';
    if (action === 'publish') return 'published to target';
    if (action === 'republish') return 'republished';
    if (action === 'validatePublish') return 'publish state validated';
    return 'unpublished';
}

function getActionDescription(action: PendingAction): string {
    if (action === 'activate') {
        return 'Activating changes this mock test from draft to published so it can be tested in the current environment.';
    }
    if (action === 'archive') {
        return 'Archiving frees this mock test\'s exam tasks for future generation.';
    }
    if (action === 'delete') {
        return 'Deleting permanently removes this mock test.';
    }
    if (action === 'publish') {
        return 'Publishing copies this already-activated mock test to the next environment using the target publish endpoint.';
    }
    if (action === 'republish') {
        return 'Republishing refreshes the same published mock test on the target environment, including title, timestamps, and exam-task links.';
    }
    if (action === 'validatePublish') {
        return 'Validate Publish checks the target environment. If the target mock test is missing while this mock test is marked Published locally, it will be reset back to Draft.';
    }
    return 'Unpublishing moves this mock test back to draft without freeing its exam tasks.';
}

function getBulkActionEmptyStateMessage(action: BulkAction): string {
    if (action === 'activate') return 'No draft mock tests selected.';
    if (action === 'publish') return 'No published mock tests selected for target publish.';
    return 'No published mock tests selected to unpublish.';
}

function getBulkActionSummary(action: BulkAction, count: number, skippedCount: number): string {
    const noun = count === 1 ? 'mock test' : 'mock tests';
    const skippedSummary = skippedCount > 0 ? ` ${skippedCount} selected ${skippedCount === 1 ? 'item will' : 'items will'} be skipped.` : '';
    return `You are about to ${getActionVerb(action)} ${count} selected ${noun}.${skippedSummary}`;
}

function buildAdminHashUrl(path: string): string {
    return `#${path}`;
}

function buildIssueExamTaskSearchHref(
    examTaskType: string | null | undefined,
    examName: string | null | undefined,
    questionNo: string | null | undefined,
): string | null {
    if (!examTaskType || !examName || !questionNo) {
        return null;
    }

    const matchedType = getExamTaskTypeByApiType(examTaskType);
    if (!matchedType) {
        return null;
    }

    const normalizedExamName = examName.trim();
    const normalizedQuestionNo = questionNo.trim();
    if (!normalizedExamName || !normalizedQuestionNo) {
        return null;
    }

    const query = new URLSearchParams({
        examName: normalizedExamName,
        questionNo: normalizedQuestionNo,
    });

    return buildAdminHashUrl(`/live-exam-tasks/${encodeURIComponent(matchedType.key)}?${query.toString()}`);
}

function getAutoPublishExamTaskItems(issues: MockTestPublishIssue[]): AutoPublishExamTaskItem[] {
    const eligibleCodes = new Set(['MissingTargetExamTaskId', 'MissingTargetQuestionNo']);
    const items = new Map<string, AutoPublishExamTaskItem>();

    for (const issue of issues) {
        if (!issue.code || !eligibleCodes.has(issue.code)) {
            continue;
        }

        const examTaskId = typeof issue.sourceExamTaskId === 'string' ? issue.sourceExamTaskId.trim() : '';
        const examTaskType = typeof issue.sourceExamTaskType === 'string' ? issue.sourceExamTaskType.trim() : '';
        if (!examTaskId) {
            continue;
        }

        if (!items.has(examTaskId)) {
            items.set(examTaskId, {
                examTaskId,
                examTaskType,
                questionNo: typeof issue.questionNo === 'string' && issue.questionNo.trim() ? issue.questionNo.trim() : null,
            });
        }
    }

    return Array.from(items.values());
}

function formatIssueReference(issue: MockTestPublishIssue): string | null {
    const parts: string[] = [];

    if (issue.questionNo) {
        parts.push(`Question ${issue.questionNo}`);
    }

    return parts.length > 0 ? parts.join(' • ') : null;
}

function isDraftStatus(status: string): boolean {
    const normalized = status.trim().toLowerCase();
    return normalized === 'draft' || normalized === 'unpublished';
}

function isPublishedStatus(status: string): boolean {
    return status.trim().toLowerCase() === 'published';
}

function isSectionalMockTestKind(mockTestKind: string): boolean {
    return mockTestKind.trim().toLowerCase() === 'sectional';
}

export function MockTestManagementPage() {
    const notify = useNotify();
    const navigate = useNavigate();

    const [items, setItems] = useState<MockTestRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filters, setFilters] = useState<FilterState>({
        examName: 'Core',
        mockTestKind: 'Sectional',
        examTaskCategory: 'Speaking',
        status: '',
        publishedState: '',
        search: '',
    });
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [generateDraft, setGenerateDraft] = useState<GenerateDraft>(emptyGenerateDraft());
    const [generationProfiles, setGenerationProfiles] = useState<GenerationProfileListItem[]>([]);
    const [generationProfilesLoading, setGenerationProfilesLoading] = useState(false);
    const [isFreeLoadingId, setIsFreeLoadingId] = useState<string | null>(null);
    const [pendingAction, setPendingAction] = useState<ActionState | null>(null);
    const [bulkPendingAction, setBulkPendingAction] = useState<BulkActionState | null>(null);
    const [targetSyncFailure, setTargetSyncFailure] = useState<TargetSyncFailureState | null>(null);
    const [resolvedIssueExamTasks, setResolvedIssueExamTasks] = useState<Record<string, ResolvedIssueExamTask>>({});
    const [autoPublishExamTasksLoading, setAutoPublishExamTasksLoading] = useState(false);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [savedScrollY, setSavedScrollY] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [bulkActionResults, setBulkActionResults] = useState<BulkActionResult[] | null>(null);

    const refreshMockTestItem = async (id: string) => {
        const response = await getMockTest(id);
        setItems((current) => current.map((entry) => (entry.id === id ? response.data : entry)));
    };

    const handleToggleSelect = (id: string) => {
        setSelectedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
    };

    const handleSelectAllVisible = () => {
        const visibleIds = visibleItems.map((it) => it.id);
        const allSelected = visibleIds.every((id) => selectedIds.includes(id)) && visibleIds.length > 0;
        if (allSelected) {
            setSelectedIds((current) => current.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedIds((current) => Array.from(new Set([...current, ...visibleIds])));
        }
    };

    const activeExamLabel = filters.examName || 'All Exams';
    const activeKindLabel =
        mockTestKindLabels[filters.mockTestKind as MockTestKind] ?? filters.mockTestKind ?? 'All Kinds';
    const filtersUseSectionCategory = isSectionalMockTestKind(filters.mockTestKind);
    const activeCategoryLabel = filtersUseSectionCategory
        ? examTaskCategories.find((category) => category.id === filters.examTaskCategory)?.label ?? 'All Categories'
        : 'All Categories';

    const visibleItems = useMemo(() => {
        return items.filter((item) => matchesStatus(item, filters.status) && matchesSearch(item, filters.search));
    }, [filters.search, filters.status, items]);

    const selectedItems = useMemo(() => {
        return items.filter((item) => selectedIds.includes(item.id));
    }, [items, selectedIds]);

    const selectedDraftCount = useMemo(() => {
        return selectedItems.filter((item) => isDraftStatus(getMockTestStatus(item))).length;
    }, [selectedItems]);

    const selectedPublishedCount = useMemo(() => {
        return selectedItems.filter((item) => isPublishedStatus(getMockTestStatus(item))).length;
    }, [selectedItems]);

    const selectedPublishableCount = useMemo(() => {
        return selectedItems.filter((item) => isPublishedStatus(getMockTestStatus(item)) && !getIsPublishedToTarget(item)).length;
    }, [selectedItems]);

    const availableStatuses = useMemo(() => {
        return Array.from(new Set(items.map((item) => getMockTestStatus(item)))).sort((left, right) =>
            left.localeCompare(right),
        );
    }, [items]);

    const statusTabs = useMemo<SegmentedTabOption[]>(
        () => [{ value: '', label: 'All Statuses' }, ...availableStatuses.map((status) => ({ value: status, label: status }))],
        [availableStatuses],
    );

    const examTabs = useMemo<SegmentedTabOption[]>(() => examNames.map((examName) => ({ value: examName, label: examName })), []);

    const kindTabs = useMemo<SegmentedTabOption[]>(
        () => mockTestKinds.map((kind) => ({ value: kind, label: mockTestKindLabels[kind] })),
        [],
    );

    const categoryTabs = useMemo<SegmentedTabOption[]>(
        () => [{ value: '', label: 'All Categories' }, ...examTaskCategories.map((category) => ({ value: category.id, label: category.label }))],
        [],
    );

    const generateKindTabs = useMemo<SegmentedTabOption[]>(
        () => mockTestKinds.filter((kind) => kind !== '').map((kind) => ({ value: kind, label: mockTestKindLabels[kind] })),
        [],
    );

    const generateCategoryTabs = useMemo<SegmentedTabOption[]>(
        () => examTaskCategories.map((category) => ({ value: category.id, label: category.label })),
        [],
    );

    const headerTitle = useMemo(() => {
        return `${activeExamLabel} ${activeKindLabel} ${activeCategoryLabel} Mock Tests`
            .replace(/\s+/g, ' ')
            .replace('All Kinds All Categories', 'All Mock Tests')
            .trim();
    }, [activeCategoryLabel, activeExamLabel, activeKindLabel]);

    const generateUsesSectionCategory = isSectionalMockTestKind(generateDraft.mockTestKind);

    useEffect(() => {
        if (!generateDialogOpen) {
            return;
        }

        let active = true;

        const loadGenerationProfiles = async () => {
            setGenerationProfilesLoading(true);
            try {
                const items = await getGenerationProfileList(generateDraft.examName);
                if (!active) return;

                setGenerationProfiles(items);
                setGenerateDraft((current) => {
                    const hasSelectedProfile = items.some((item) => item.id === current.generationProfileId);
                    return {
                        ...current,
                        generationProfileId: hasSelectedProfile ? current.generationProfileId : (items[0]?.id ?? ''),
                    };
                });
            } catch (error) {
                if (!active) return;
                setGenerationProfiles([]);
                setGenerateDraft((current) => ({ ...current, generationProfileId: '' }));
                notify(error instanceof Error ? error.message : 'Failed to load generation profiles', {
                    type: 'error',
                });
            } finally {
                if (active) {
                    setGenerationProfilesLoading(false);
                }
            }
        };

        void loadGenerationProfiles();

        return () => {
            active = false;
        };
    }, [generateDialogOpen, generateDraft.examName, notify]);

    useEffect(() => {
        let active = true;

        const load = async () => {
            setLoading(true);
            try {
                const response = await getMockTestList({
                    pageNo: page + 1,
                    pageSize: rowsPerPage,
                    examName: filters.examName,
                    mockTestKind: filters.mockTestKind,
                    ...(filtersUseSectionCategory ? { examTaskCategory: filters.examTaskCategory } : {}),
                    ...(filters.publishedState === 'published'
                        ? { isPublishedToTarget: true }
                        : filters.publishedState === 'unpublished'
                          ? { isPublishedToTarget: false }
                          : {}),
                    sort: 'updatedAt',
                    order: 'DESC',
                });

                if (!active) return;
                setItems(response.data);
                setTotal(response.total);
                // restore scroll if requested
                if (savedScrollY !== null) {
                    // use a short timeout to allow DOM to render
                    setTimeout(() => {
                        try {
                            window.scrollTo({ top: savedScrollY ?? 0 });
                        } catch (e) {
                            // ignore
                        }
                    }, 50);
                    setSavedScrollY(null);
                }
            } catch (error) {
                if (!active) return;
                setItems([]);
                setTotal(0);
                notify(error instanceof Error ? error.message : 'Failed to load mock tests', {
                    type: 'error',
                });
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            active = false;
        };
    }, [filters.examName, filters.examTaskCategory, filters.mockTestKind, filters.publishedState, filtersUseSectionCategory, notify, page, reloadVersion, rowsPerPage]);

    const handleOpenDetail = (item: MockTestRecord) => {
        window.open(buildMockTestDetailUrl(item.id), '_blank', 'noopener,noreferrer');
    };

    const handleRequestBulkAction = (action: BulkAction) => {
        const eligibleItems = selectedItems
            .filter((item) => {
                if (action === 'activate') {
                    return isDraftStatus(getMockTestStatus(item));
                }

                if (action === 'publish') {
                    return isPublishedStatus(getMockTestStatus(item)) && !getIsPublishedToTarget(item);
                }

                return isPublishedStatus(getMockTestStatus(item));
            })
            .map((item) => ({ id: item.id, title: getMockTestTitle(item) }));

        if (!eligibleItems.length) {
            notify(getBulkActionEmptyStateMessage(action), { type: 'info' });
            return;
        }

        setBulkPendingAction({
            action,
            items: eligibleItems,
            skippedCount: selectedItems.length - eligibleItems.length,
        });
    };

    const runBulkActionItem = async (action: BulkAction, item: BulkActionItem): Promise<BulkActionResult> => {
        try {
            if (action === 'activate') {
                await activateMockTest(item.id);
                return {
                    id: item.id,
                    title: item.title,
                    success: true,
                };
            }

            if (action === 'publish') {
                const response = (await publishMockTestToTarget(item.id)) as MockTestTargetSyncResponse;
                if (response.success === false) {
                    return {
                        id: item.id,
                        title: item.title,
                        success: false,
                        errorMessage: response.errorMessage ?? response.message ?? 'Publish validation failed.',
                        issues: response.data?.issues ?? null,
                        rawResponse: response,
                    };
                }

                return {
                    id: item.id,
                    title: item.title,
                    success: true,
                    issues: response.data?.issues ?? null,
                };
            }

            await unpublishMockTest(item.id);
            return {
                id: item.id,
                title: item.title,
                success: true,
            };
        } catch (error) {
            return {
                id: item.id,
                title: item.title,
                success: false,
                errorMessage: error instanceof Error ? error.message : 'Mock test action failed',
            };
        }
    };

    const handleAction = async () => {
        if (!pendingAction) return;

        setActionLoading(pendingAction.id);
        try {
            if (pendingAction.action === 'activate') {
                await activateMockTest(pendingAction.id);
                await refreshMockTestItem(pendingAction.id);
            } else if (pendingAction.action === 'archive') {
                await archiveMockTest(pendingAction.id);
            } else if (pendingAction.action === 'delete') {
                await deleteMockTest(pendingAction.id);
            } else if (pendingAction.action === 'validatePublish') {
                const response = (await validatePublishMockTest(pendingAction.id)) as MockTestTargetSyncResponse;
                if (response && typeof response === 'object' && 'success' in response && response.success === false) {
                    setTargetSyncFailure({
                        actionLabel: 'Validate Publish',
                        title: pendingAction.title,
                        errorMessage: response.errorMessage ?? 'Validate publish failed.',
                        issues: response.data?.issues ?? [],
                    });
                    setPendingAction(null);
                    return;
                }
            } else if (pendingAction.action === 'publish') {
                const response = (await publishMockTestToTarget(pendingAction.id)) as MockTestTargetSyncResponse;
                if (response.success === false) {
                    setTargetSyncFailure({
                        actionLabel: 'Publish',
                        title: pendingAction.title,
                        errorMessage: response.errorMessage ?? 'Publish validation failed.',
                        issues: response.data?.issues ?? [],
                    });
                    setPendingAction(null);
                    return;
                }
                await refreshMockTestItem(pendingAction.id);
            } else if (pendingAction.action === 'republish') {
                const response = (await republishMockTestToTarget(pendingAction.id)) as MockTestTargetSyncResponse;
                if (response.success === false) {
                    setTargetSyncFailure({
                        actionLabel: 'Republish',
                        title: pendingAction.title,
                        errorMessage: response.errorMessage ?? 'Republish validation failed.',
                        issues: response.data?.issues ?? [],
                    });
                    setPendingAction(null);
                    return;
                }
                await refreshMockTestItem(pendingAction.id);
            } else {
                await unpublishMockTest(pendingAction.id);
            }

            notify(`${pendingAction.title} ${getActionPastTense(pendingAction.action)} successfully`, {
                type: 'info',
            });
            setPendingAction(null);
            if (
                pendingAction.action !== 'activate' &&
                pendingAction.action !== 'publish' &&
                pendingAction.action !== 'republish'
            ) {
                setReloadVersion((current) => current + 1);
            }
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Mock test action failed', {
                type: 'error',
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleBulkAction = async () => {
        if (!bulkPendingAction) return;

        try {
            setSavedScrollY(window.scrollY ?? 0);
        } catch (error) {
            // ignore scroll persistence failures
        }

        setActionLoading(`bulk-${bulkPendingAction.action}`);
        try {
            const results: BulkActionResult[] = [];

            for (const item of bulkPendingAction.items) {
                results.push(await runBulkActionItem(bulkPendingAction.action, item));
            }

            const successCount = results.filter((result) => result.success).length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                notify(
                    `${successCount} mock test${successCount === 1 ? '' : 's'} ${getActionPastTense(bulkPendingAction.action)} successfully`,
                    { type: 'info' },
                );
            }

            if (failCount > 0) {
                notify(`${failCount} mock test${failCount === 1 ? '' : 's'} failed to ${getActionVerb(bulkPendingAction.action)}`, {
                    type: 'warning',
                });
                setBulkActionResults(results);
            }

            setBulkPendingAction(null);
            setSelectedIds([]);
            setReloadVersion((current) => current + 1);
        } finally {
            setActionLoading(null);
        }
    };

    const handleGenerate = async () => {
        if (!generateDraft.generationProfileId) {
            notify('Select a generation profile before generating a mock test.', {
                type: 'warning',
            });
            return;
        }

        setGenerateLoading(true);
        try {
            const generatePayload = {
                examName: generateDraft.examName,
                generationProfileId: generateDraft.generationProfileId,
                mockTestKind: generateDraft.mockTestKind,
                isFree: generateDraft.isFree === 'true',
                ...(generateUsesSectionCategory ? { sectionCategory: generateDraft.sectionCategory } : {}),
            };

            await generateMockTest({
                ...generatePayload,
            });

            notify(
                [
                    generateDraft.examName,
                    generateDraft.mockTestKind,
                    generateUsesSectionCategory ? generateDraft.sectionCategory : '',
                    formatAccessLabel(generateDraft.isFree === 'true'),
                    'mock test generated',
                ]
                    .filter(Boolean)
                    .join(' '),
                {
                type: 'info',
                },
            );
            setFilters((current) => ({
                ...current,
                examName: generateDraft.examName,
                mockTestKind: generateDraft.mockTestKind,
                examTaskCategory: generateUsesSectionCategory ? generateDraft.sectionCategory : '',
            }));
            setPage(0);
            setGenerateDialogOpen(false);
            setReloadVersion((current) => current + 1);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to generate mock test', {
                type: 'error',
            });
        } finally {
            setGenerateLoading(false);
        }
    };

    const handleIsFreeUpdate = async (item: MockTestRecord, nextValue: IsFreeValue) => {
        const nextIsFree = nextValue === 'true';
        if (getIsFreeValue(item) === nextIsFree) {
            return;
        }

        setIsFreeLoadingId(item.id);
        try {
            await updateMockTest(item.id, { isFree: nextIsFree });
            setItems((current) => current.map((entry) => (entry.id === item.id ? { ...entry, isFree: nextIsFree } : entry)));
            notify(`${getMockTestTitle(item)} marked as ${formatAccessLabel(nextIsFree).toLowerCase()}.`, { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to update mock test access', {
                type: 'error',
            });
        } finally {
            setIsFreeLoadingId(null);
        }
    };

    const handleAutoPublishMissingExamTasks = async () => {
        if (!targetSyncFailure) {
            return;
        }

        const items = getAutoPublishExamTaskItems(targetSyncFailure.issues);
        if (!items.length) {
            notify('No missing exam tasks were found for auto publish.', { type: 'info' });
            return;
        }

        setAutoPublishExamTasksLoading(true);
        try {
            const failures: string[] = [];

            for (const item of items) {
                try {
                    await updateExamTask(EXAM_TASK_DESTINATION_RESOURCE, {
                        id: item.examTaskId,
                        data: {},
                        previousData: { id: item.examTaskId },
                        meta: {
                            action: 'publish',
                            actionPath: 'publish',
                            method: 'POST',
                        },
                    });
                } catch (error) {
                    failures.push(item.questionNo ?? item.examTaskId);
                }
            }

            if (failures.length > 0) {
                notify(
                    failures.length === items.length
                        ? 'Failed to auto publish the missing exam tasks.'
                        : `${failures.length} missing exam task${failures.length === 1 ? '' : 's'} failed to publish.`,
                    { type: 'warning' },
                );
                return;
            }

            notify('All exam tasks are published, retry publish mock test.', { type: 'info' });
        } finally {
            setAutoPublishExamTasksLoading(false);
        }
    };

    useEffect(() => {
        if (!targetSyncFailure) {
            setResolvedIssueExamTasks({});
            return;
        }

        const sourceExamTaskIds = Array.from(
            new Set(
                targetSyncFailure.issues
                    .map((issue) => (typeof issue.sourceExamTaskId === 'string' ? issue.sourceExamTaskId.trim() : ''))
                    .filter(Boolean),
            ),
        );

        if (sourceExamTaskIds.length === 0) {
            setResolvedIssueExamTasks({});
            return;
        }

        let active = true;

        const load = async () => {
            const entries = await Promise.all(
                sourceExamTaskIds.map(async (examTaskId) => {
                    try {
                        const response = await getExamTask(EXAM_TASK_DESTINATION_RESOURCE, { id: examTaskId });
                        const record = response.data;

                        return [
                            examTaskId,
                            {
                                examTaskType:
                                    typeof record.examTaskType === 'string' && record.examTaskType.trim()
                                        ? record.examTaskType.trim()
                                        : null,
                                examName:
                                    typeof record.examName === 'string' && record.examName.trim() ? record.examName.trim() : null,
                                questionNo:
                                    typeof record.questionNo === 'string' && record.questionNo.trim()
                                        ? record.questionNo.trim()
                                        : null,
                            } satisfies ResolvedIssueExamTask,
                        ] as const;
                    } catch {
                        return [examTaskId, { examTaskType: null, examName: null, questionNo: null } satisfies ResolvedIssueExamTask] as const;
                    }
                }),
            );

            if (!active) {
                return;
            }

            setResolvedIssueExamTasks(Object.fromEntries(entries));
        };

        void load();

        return () => {
            active = false;
        };
    }, [targetSyncFailure]);

    return (
        <Card sx={{ width: '100%' }}>
            <Title title="Mock Test Control Center" />
            <CardContent>
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                    >
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Mock Test Control Center
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {headerTitle}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => {
                                setGenerateDraft(emptyGenerateDraft());
                                setGenerateDialogOpen(true);
                            }}
                        >
                            Generate Mock Test
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                navigate('/mock-test-generation-profiles');
                            }}
                        >
                            Manage Profiles
                        </Button>
                    </Stack>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Review mock tests by exam, kind, and exam task category, then generate,
                            activate, publish to target, republish, unpublish, archive, or delete them directly from the table.
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            label="Search Loaded Results"
                            size="small"
                            fullWidth
                            value={filters.search}
                            onChange={(event) => {
                                setPage(0);
                                setFilters((current) => ({ ...current, search: event.target.value }));
                            }}
                            helperText="Searches the currently loaded page of mock tests."
                        />
                        <Box sx={{ width: '100%' }}>
                            <SegmentedTabs
                                label="Status"
                                value={filters.status}
                                options={statusTabs}
                                onChange={(nextValue) => {
                                    setPage(0);
                                    setFilters((current) => ({ ...current, status: nextValue }));
                                }}
                            />
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <SegmentedTabs
                                label="Target Publish"
                                value={filters.publishedState}
                                options={targetPublishStateTabs}
                                onChange={(nextValue) => {
                                    setPage(0);
                                    setFilters((current) => ({ ...current, publishedState: nextValue }));
                                }}
                            />
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'flex-start',
                            '& > *': {
                                flex: '1 1 320px',
                                minWidth: 280,
                            },
                        }}
                    >
                        <SegmentedTabs
                            label="Exam"
                            value={filters.examName}
                            options={examTabs}
                            onChange={(nextValue) => {
                                setPage(0);
                                setFilters((current) => ({ ...current, examName: nextValue }));
                            }}
                        />

                        <SegmentedTabs
                            label="Mock Test Kind"
                            value={filters.mockTestKind}
                            options={kindTabs}
                            onChange={(nextValue) => {
                                setPage(0);
                                setFilters((current) => ({
                                    ...current,
                                    mockTestKind: nextValue,
                                    examTaskCategory: isSectionalMockTestKind(nextValue)
                                        ? current.examTaskCategory || 'Speaking'
                                        : '',
                                }));
                            }}
                        />

                        <SegmentedTabs
                            label="Exam Task Category"
                            value={filters.examTaskCategory}
                            options={categoryTabs}
                            disabled={!filtersUseSectionCategory}
                            onChange={(nextValue) => {
                                setPage(0);
                                setFilters((current) => ({ ...current, examTaskCategory: nextValue }));
                            }}
                            helperText={
                                filtersUseSectionCategory
                                    ? 'Applies to sectional mock tests only.'
                                    : 'Full mock tests are not filtered by exam task category.'
                            }
                        />
                    </Box>

                    {loading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading mock tests...
                            </Typography>
                        </Stack>
                    ) : items.length === 0 ? (
                        <Alert severity="info">No mock tests found for the selected filters.</Alert>
                    ) : visibleItems.length === 0 ? (
                        <Alert severity="info">No loaded mock tests match the current search and status filters.</Alert>
                    ) : (
                        <>
                            <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                                {visibleItems.map((item) => {
                                    const title = getMockTestTitle(item);
                                    const subtitle = getMockTestSubtitle(item);
                                    const status = getMockTestStatus(item);
                                    const isPublishedToTarget = getIsPublishedToTarget(item);
                                    const showActivateAction = isDraftStatus(status);
                                    const showPublishAction = isPublishedStatus(status) && !isPublishedToTarget;
                                    const showRepublishAction = isPublishedStatus(status) && isPublishedToTarget;
                                    const showValidatePublishAction = isPublishedStatus(status);
                                    const isMutating = actionLoading === item.id;
                                    const isAccessUpdating = isFreeLoadingId === item.id;
                                    const metrics = getSummaryMetrics(item);
                                    const sectionsMetric = metrics.find((metric) => metric.label === 'Sections');
                                    const durationMetric = metrics.find((metric) => metric.label === 'Duration');
                                    const isFree = getIsFreeValue(item);

                                    return (
                                        <Card key={item.id} variant="outlined">
                                            <CardContent>
                                                <Stack spacing={2}>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                                            {title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                            {subtitle}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack spacing={0.75}>
                                                        <Typography variant="body2">Exam: {formatValue(item.examName)}</Typography>
                                                        <Typography variant="body2">Kind: {formatValue(item.mockTestKind)}</Typography>
                                                        <Typography variant="body2">Access: {formatAccessLabel(isFree)}</Typography>
                                                        <Typography variant="body2">Sections: {formatValue(sectionsMetric?.value, '-')}</Typography>
                                                        <Typography variant="body2">
                                                            Duration: {durationMetric ? `${durationMetric.value} ${durationMetric.suffix ?? ''}`.trim() : '-'}
                                                        </Typography>
                                                        <Typography variant="body2">Version: {formatValue(item.version ?? item.revision, 'N/A')}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Created: {formatDateTime(item.createdAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Published: {formatDateTime(item.publishedAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Updated: {formatDateTime(item.updatedAt)}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        spacing={1}
                                                        useFlexGap
                                                        flexWrap="wrap"
                                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                    >
                                                        <Chip label={`Status: ${status}`} color="primary" variant="outlined" size="small" />
                                                        <Chip
                                                            label={`Target: ${isPublishedToTarget ? 'Published' : 'Unpublished'}`}
                                                            color={isPublishedToTarget ? 'success' : 'default'}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                        <Chip label={`Access: ${formatAccessLabel(isFree)}`} color={isFree ? 'success' : 'default'} variant="outlined" size="small" />
                                                        <Button variant="outlined" size="small" onClick={() => handleOpenDetail(item)}>
                                                            View
                                                        </Button>
                                                        {showActivateAction ? (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<PublishOutlinedIcon />}
                                                                sx={publishActionButtonSx}
                                                                disabled={isMutating}
                                                                onClick={() => {
                                                                    setPendingAction({ id: item.id, action: 'activate', title });
                                                                }}
                                                            >
                                                                Activate
                                                            </Button>
                                                        ) : null}
                                                        {showPublishAction ? (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                disabled={isMutating}
                                                                onClick={() => {
                                                                    setPendingAction({ id: item.id, action: 'publish', title });
                                                                }}
                                                            >
                                                                Publish
                                                            </Button>
                                                        ) : null}
                                                        {isPublishedStatus(status) ? (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<UnpublishedOutlinedIcon />}
                                                                disabled={isMutating}
                                                                onClick={() => {
                                                                    setPendingAction({ id: item.id, action: 'unpublish', title });
                                                                }}
                                                            >
                                                                UnPublish
                                                            </Button>
                                                        ) : null}
                                                        {showRepublishAction ? (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<SyncOutlinedIcon />}
                                                                disabled={isMutating}
                                                                onClick={() => {
                                                                    setPendingAction({ id: item.id, action: 'republish', title });
                                                                }}
                                                            >
                                                                Republish
                                                            </Button>
                                                        ) : null}
                                                        {showValidatePublishAction ? (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={isMutating}
                                                                onClick={() => {
                                                                    setPendingAction({ id: item.id, action: 'validatePublish', title });
                                                                }}
                                                            >
                                                                Validate Publish
                                                            </Button>
                                                        ) : null}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="warning"
                                                            startIcon={<ArchiveOutlinedIcon />}
                                                            disabled={isMutating}
                                                            onClick={() => {
                                                                setPendingAction({ id: item.id, action: 'archive', title });
                                                            }}
                                                        >
                                                            Archive
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="error"
                                                            startIcon={<DeleteOutlineIcon />}
                                                            disabled={isMutating}
                                                            onClick={() => {
                                                                setPendingAction({ id: item.id, action: 'delete', title });
                                                            }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Stack>

                                                    <Box sx={{ width: '100%' }}>
                                                        <SegmentedTabs
                                                            label="Access"
                                                            value={toIsFreeValue(isFree)}
                                                            options={accessTabs}
                                                            onChange={(nextValue) => {
                                                                void handleIsFreeUpdate(item, nextValue as IsFreeValue);
                                                            }}
                                                            disabled={isAccessUpdating}
                                                            helperText={isAccessUpdating ? 'Updating access...' : 'Admins can change this at any time.'}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>

                            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'No selection'}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<PublishOutlinedIcon />}
                                        sx={publishActionButtonSx}
                                        disabled={selectedIds.length === 0 || selectedDraftCount === 0 || actionLoading !== null}
                                        onClick={() => handleRequestBulkAction('activate')}
                                    >
                                        Bulk Activate
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={selectedIds.length === 0 || selectedPublishableCount === 0 || actionLoading !== null}
                                        onClick={() => handleRequestBulkAction('publish')}
                                    >
                                        Bulk Publish
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<UnpublishedOutlinedIcon />}
                                        disabled={selectedIds.length === 0 || selectedPublishedCount === 0 || actionLoading !== null}
                                        onClick={() => handleRequestBulkAction('unpublish')}
                                    >
                                        Bulk Unpublish
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={selectedIds.length === 0 || actionLoading !== null}
                                        onClick={async () => {
                                            // Bulk validate publish for selected ids and collect detailed results
                                            try {
                                                setSavedScrollY(window.scrollY ?? 0);
                                            } catch (e) {
                                                // ignore
                                            }
                                            setActionLoading('bulk-validate');
                                            try {
                                                const results: BulkActionResult[] = [];
                                                for (const id of selectedIds) {
                                                    const item = items.find((it) => it.id === id);
                                                    const title = item ? getMockTestTitle(item) : id;
                                                    try {
                                                        const resp = (await validatePublishMockTest(id)) as MockTestTargetSyncResponse | null | undefined;
                                                        if (resp && typeof resp === 'object' && 'success' in resp) {
                                                            if (resp.success === false) {
                                                                results.push({ id, title, success: false, errorMessage: resp.errorMessage ?? resp.message ?? 'Validation failed', issues: resp.data?.issues ?? null, rawResponse: resp });
                                                            } else {
                                                                // success true
                                                                results.push({ id, title, success: true, issues: resp.data?.issues ?? null, rawResponse: resp });
                                                            }
                                                        } else {
                                                            // Non-envelope response - attach raw response and treat as success
                                                            results.push({ id, title, success: true, rawResponse: resp });
                                                        }
                                                    } catch (error) {
                                                        results.push({ id, title, success: false, errorMessage: error instanceof Error ? error.message : String(error) });
                                                    }
                                                }

                                                setBulkActionResults(results);
                                                const successCount = results.filter((r) => r.success).length;
                                                const failCount = results.length - successCount;
                                                if (successCount > 0) {
                                                    notify(`${successCount} mock tests validated successfully`, { type: 'info' });
                                                }
                                                if (failCount > 0) {
                                                    notify(`${failCount} mock tests failed validation`, { type: 'warning' });
                                                }
                                                setReloadVersion((v) => v + 1);
                                            } finally {
                                                setActionLoading(null);
                                                setSelectedIds([]);
                                            }
                                        }}
                                        sx={publishActionButtonSx}
                                    >
                                        Validate Publish Selected
                                    </Button>
                                </Stack>
                                <Box />
                            </Box>

                            <TableContainer
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    display: { xs: 'none', lg: 'block' },
                                    width: '100%',
                                    overflowX: 'hidden',
                                }}
                            >
                                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 48 }} padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    indeterminate={
                                                        visibleItems.length > 0 && selectedIds.length > 0 && visibleItems.some((it) => !selectedIds.includes(it.id))
                                                    }
                                                    checked={visibleItems.length > 0 && visibleItems.every((it) => selectedIds.includes(it.id))}
                                                    onChange={() => handleSelectAllVisible()}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ width: '34%' }}>Mock Test</TableCell>
                                            <TableCell sx={{ width: '10%' }}>Exam</TableCell>
                                            <TableCell sx={{ width: '12%' }}>Kind</TableCell>
                                            <TableCell align="right" sx={{ width: '8%' }}>
                                                Sections
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: '10%' }}>
                                                Duration
                                            </TableCell>
                                            <TableCell sx={{ width: '8%' }}>Version</TableCell>
                                            <TableCell sx={{ width: '18%' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleItems.map((item) => {
                                            const title = getMockTestTitle(item);
                                            const subtitle = getMockTestSubtitle(item);
                                            const status = getMockTestStatus(item);
                                            const isPublishedToTarget = getIsPublishedToTarget(item);
                                            const showActivateAction = isDraftStatus(status);
                                            const showPublishAction = isPublishedStatus(status) && !isPublishedToTarget;
                                            const showRepublishAction = isPublishedStatus(status) && isPublishedToTarget;
                                            const showValidatePublishAction = isPublishedStatus(status);
                                            const isMutating = actionLoading === item.id;
                                            const isAccessUpdating = isFreeLoadingId === item.id;
                                            const metrics = getSummaryMetrics(item);
                                            const sectionsMetric = metrics.find((metric) => metric.label === 'Sections');
                                            const durationMetric = metrics.find((metric) => metric.label === 'Duration');
                                            const isFree = getIsFreeValue(item);

                                            return [
                                                <TableRow
                                                    key={`${item.id}-summary`}
                                                    hover
                                                    onClick={() => handleOpenDetail(item)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '& > *': {
                                                            borderBottom: 'none',
                                                            verticalAlign: 'top',
                                                        },
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedIds.includes(item.id)}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            onChange={() => handleToggleSelect(item.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                                sx={{ overflowWrap: 'anywhere' }}
                                                            >
                                                                {title}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ overflowWrap: 'anywhere' }}
                                                            >
                                                                {subtitle}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>{formatValue(item.examName)}</TableCell>
                                                    <TableCell>{formatValue(item.mockTestKind)}</TableCell>
                                                    <TableCell align="right">{formatValue(sectionsMetric?.value, '-')}</TableCell>
                                                    <TableCell align="right">
                                                        {durationMetric ? `${durationMetric.value} ${durationMetric.suffix ?? ''}`.trim() : '-'}
                                                    </TableCell>
                                                    <TableCell>{formatValue(item.version ?? item.revision, 'N/A')}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                            <Chip label={status} color="primary" variant="outlined" size="small" />
                                                            <Chip
                                                                label={isPublishedToTarget ? 'Target Published' : 'Target Unpublished'}
                                                                color={isPublishedToTarget ? 'success' : 'default'}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>,
                                                <TableRow
                                                    key={`${item.id}-details`}
                                                    sx={{
                                                        backgroundColor: 'action.hover',
                                                        '& > *': {
                                                            borderBottomColor: 'divider',
                                                            verticalAlign: 'top',
                                                        },
                                                    }}
                                                >
                                                    <TableCell colSpan={8} sx={{ pt: 0.5, pb: 2 }}>
                                                        <Stack spacing={1.5}>
                                                            <Box sx={{ width: '100%', maxWidth: 360 }}>
                                                                <SegmentedTabs
                                                                    label="Access"
                                                                    value={toIsFreeValue(isFree)}
                                                                    options={accessTabs}
                                                                    onChange={(nextValue) => {
                                                                        void handleIsFreeUpdate(item, nextValue as IsFreeValue);
                                                                    }}
                                                                    disabled={isAccessUpdating}
                                                                    helperText={isAccessUpdating ? 'Updating access...' : 'Admins can change this at any time.'}
                                                                />
                                                            </Box>

                                                            <Stack
                                                                direction="row"
                                                                spacing={1.5}
                                                                useFlexGap
                                                                flexWrap="wrap"
                                                                alignItems="center"
                                                            >
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Created: {formatDateTime(item.createdAt)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Published: {formatDateTime(item.publishedAt)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Updated: {formatDateTime(item.updatedAt)}
                                                                </Typography>
                                                            </Stack>

                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                useFlexGap
                                                                flexWrap="wrap"
                                                                alignItems="center"
                                                            >
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => handleOpenDetail(item)}
                                                                >
                                                                    View
                                                                </Button>
                                                                {showActivateAction ? (
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        startIcon={<PublishOutlinedIcon />}
                                                                        sx={publishActionButtonSx}
                                                                        disabled={isMutating}
                                                                        onClick={() => {
                                                                            setPendingAction({
                                                                                id: item.id,
                                                                                action: 'activate',
                                                                                title,
                                                                            });
                                                                        }}
                                                                    >
                                                                        Activate
                                                                    </Button>
                                                                ) : null}
                                                                {showPublishAction ? (
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        disabled={isMutating}
                                                                        onClick={() => {
                                                                            setPendingAction({
                                                                                id: item.id,
                                                                                action: 'publish',
                                                                                title,
                                                                            });
                                                                        }}
                                                                    >
                                                                        Publish
                                                                    </Button>
                                                                ) : null}
                                                                {isPublishedStatus(status) ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        startIcon={<UnpublishedOutlinedIcon />}
                                                                        disabled={isMutating}
                                                                        onClick={() => {
                                                                            setPendingAction({
                                                                                id: item.id,
                                                                                action: 'unpublish',
                                                                                title,
                                                                            });
                                                                        }}
                                                                    >
                                                                        UnPublish
                                                                    </Button>
                                                                ) : null}
                                                                {showRepublishAction ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        startIcon={<SyncOutlinedIcon />}
                                                                        disabled={isMutating}
                                                                        onClick={() => {
                                                                            setPendingAction({
                                                                                id: item.id,
                                                                                action: 'republish',
                                                                                title,
                                                                            });
                                                                        }}
                                                                    >
                                                                        Republish
                                                                    </Button>
                                                                ) : null}
                                                                {showValidatePublishAction ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={isMutating}
                                                                        onClick={() => {
                                                                            setPendingAction({
                                                                                id: item.id,
                                                                                action: 'validatePublish',
                                                                                title,
                                                                            });
                                                                        }}
                                                                    >
                                                                        Validate Publish
                                                                    </Button>
                                                                ) : null}
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    color="warning"
                                                                    startIcon={<ArchiveOutlinedIcon />}
                                                                    disabled={isMutating}
                                                                    onClick={() => {
                                                                        setPendingAction({
                                                                            id: item.id,
                                                                            action: 'archive',
                                                                            title,
                                                                        });
                                                                    }}
                                                                >
                                                                    Archive
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    color="error"
                                                                    startIcon={<DeleteOutlineIcon />}
                                                                    disabled={isMutating}
                                                                    onClick={() => {
                                                                        setPendingAction({
                                                                            id: item.id,
                                                                            action: 'delete',
                                                                            title,
                                                                        });
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Stack>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>,
                                            ];
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_event, nextPage) => setPage(nextPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(Number(event.target.value));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[25, 50, 100]}
                            />
                        </>
                    )}
                </Stack>
            </CardContent>

            <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Mock Test</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Create a new mock test using the admin generation endpoint. Sectional mock tests
                            use the selected exam task category.
                        </Typography>
                        <SegmentedTabs
                            label="Exam"
                            value={generateDraft.examName}
                            options={examTabs}
                            onChange={(nextValue) => {
                                setGenerateDraft((current) => ({ ...current, examName: nextValue, generationProfileId: '' }));
                            }}
                        />
                        {generationProfilesLoading ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading generation profiles...
                                </Typography>
                            </Stack>
                        ) : generationProfiles.length === 0 ? (
                            <Alert
                                severity="warning"
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            setGenerateDialogOpen(false);
                                            navigate('/mock-test-generation-profiles');
                                        }}
                                    >
                                        Create Profile
                                    </Button>
                                }
                            >
                                No generation profiles exist for {generateDraft.examName}. Create one before generating mock tests.
                            </Alert>
                        ) : (
                            <TextField
                                label="Generation Profile"
                                select
                                fullWidth
                                value={generateDraft.generationProfileId}
                                onChange={(event) => {
                                    const nextValue = event.target.value;
                                    setGenerateDraft((current) => ({ ...current, generationProfileId: nextValue }));
                                }}
                                helperText="Profiles control section order, task type order, and planned counts."
                            >
                                {generationProfiles.map((profile) => (
                                    <MenuItem key={profile.id} value={profile.id}>
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        <SegmentedTabs
                            label="Mock Test Kind"
                            value={generateDraft.mockTestKind}
                            options={generateKindTabs}
                            onChange={(nextValue) => {
                                setGenerateDraft((current) => ({ ...current, mockTestKind: nextValue }));
                            }}
                        />
                        <SegmentedTabs
                            label="Access"
                            value={generateDraft.isFree}
                            options={accessTabs}
                            onChange={(nextValue) => {
                                setGenerateDraft((current) => ({ ...current, isFree: nextValue as IsFreeValue }));
                            }}
                            helperText="Choose whether the generated mock test should be free or paid."
                        />
                        {generateUsesSectionCategory ? (
                            <SegmentedTabs
                                label="Exam Task Category"
                                value={generateDraft.sectionCategory}
                                options={generateCategoryTabs}
                                onChange={(nextValue) => {
                                    setGenerateDraft((current) => ({ ...current, sectionCategory: nextValue }));
                                }}
                                helperText="This is sent as sectionCategory in the generate request body."
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenerateDialogOpen(false)} disabled={generateLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleGenerate()}
                        variant="contained"
                        disabled={generateLoading || generationProfilesLoading || generationProfiles.length === 0}
                    >
                        {generateLoading ? 'Generating...' : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pendingAction !== null} onClose={() => setPendingAction(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {pendingAction ? `${getActionLabel(pendingAction.action)} Mock Test` : 'Mock Test Action'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body1">
                            {pendingAction ? `Are you sure you want to ${getActionVerb(pendingAction.action)} ${pendingAction.title}?` : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pendingAction ? getActionDescription(pendingAction.action) : ''}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingAction(null)} disabled={actionLoading !== null}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleAction()}
                        variant="contained"
                        color={pendingAction?.action === 'delete' ? 'error' : 'primary'}
                        sx={pendingAction?.action === 'activate' ? publishActionButtonSx : undefined}
                        disabled={actionLoading !== null}
                    >
                        {pendingAction ? getActionLabel(pendingAction.action) : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={bulkPendingAction !== null} onClose={() => setBulkPendingAction(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {bulkPendingAction ? `Bulk ${getActionLabel(bulkPendingAction.action)} Mock Tests` : 'Bulk Mock Test Action'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body1">
                            {bulkPendingAction
                                ? getBulkActionSummary(
                                      bulkPendingAction.action,
                                      bulkPendingAction.items.length,
                                      bulkPendingAction.skippedCount,
                                  )
                                : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {bulkPendingAction ? getActionDescription(bulkPendingAction.action) : ''}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkPendingAction(null)} disabled={actionLoading !== null}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleBulkAction()}
                        variant="contained"
                        sx={bulkPendingAction?.action === 'activate' ? publishActionButtonSx : undefined}
                        disabled={actionLoading !== null}
                    >
                        {bulkPendingAction ? `Bulk ${getActionLabel(bulkPendingAction.action)}` : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={targetSyncFailure !== null} onClose={() => setTargetSyncFailure(null)} maxWidth="md" fullWidth>
                <DialogTitle>{targetSyncFailure ? `${targetSyncFailure.actionLabel} Failed` : 'Target Publish Failed'}</DialogTitle>
                <DialogContent>
                    {targetSyncFailure ? (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <Alert severity="warning">{targetSyncFailure.errorMessage}</Alert>
                            <Typography variant="body2" color="text.secondary">
                                {targetSyncFailure.title}
                            </Typography>
                            {targetSyncFailure.issues.length > 0 ? (
                                <Stack spacing={1.25}>
                                    {targetSyncFailure.issues.map((issue, index) => {
                                        const resolvedIssueExamTask =
                                            typeof issue.sourceExamTaskId === 'string'
                                                ? resolvedIssueExamTasks[issue.sourceExamTaskId.trim()]
                                                : undefined;
                                        const sourceExamTaskHref = buildIssueExamTaskSearchHref(
                                            issue.sourceExamTaskType ?? resolvedIssueExamTask?.examTaskType,
                                            resolvedIssueExamTask?.examName,
                                            issue.questionNo ?? resolvedIssueExamTask?.questionNo,
                                        );
                                        const questionLinkLabel = issue.questionNo?.trim() || resolvedIssueExamTask?.questionNo || 'Question';

                                        return (
                                            <Box key={`${issue.code ?? 'issue'}-${index}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                                <Stack spacing={0.75}>
                                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                                        <Typography variant="subtitle2">{issue.code || 'Issue'}</Typography>
                                                        {sourceExamTaskHref ? (
                                                            <Chip
                                                                label={`Question ${questionLinkLabel}`}
                                                                size="small"
                                                                variant="outlined"
                                                                clickable
                                                                component="a"
                                                                href={sourceExamTaskHref}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            />
                                                        ) : (
                                                            formatIssueReference(issue) ? <Chip label={formatIssueReference(issue) ?? ''} size="small" variant="outlined" /> : null
                                                        )}
                                                    </Stack>
                                                    <Typography variant="body2">{issue.message || 'No issue message provided.'}</Typography>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Alert severity="info">No issue details were returned by the API.</Alert>
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {targetSyncFailure && getAutoPublishExamTaskItems(targetSyncFailure.issues).length > 0 ? (
                        <Button
                            onClick={() => void handleAutoPublishMissingExamTasks()}
                            variant="contained"
                            disabled={autoPublishExamTasksLoading}
                            sx={publishActionButtonSx}
                        >
                            {autoPublishExamTasksLoading ? 'Auto Publishing Exam Tasks...' : 'Auto Publish Exam Task'}
                        </Button>
                    ) : null}
                    <Button onClick={() => setTargetSyncFailure(null)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={bulkActionResults !== null} onClose={() => setBulkActionResults(null)} maxWidth="md" fullWidth>
                <DialogTitle>Bulk Action Results</DialogTitle>
                <DialogContent>
                    {bulkActionResults ? (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            {bulkActionResults.map((res) => (
                                <Box key={res.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                    <Stack spacing={0.75}>
                                        {/* Show issues first */}
                                        {res.issues && res.issues.length > 0 ? (
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2">Issues</Typography>
                                                {res.issues.map((issue, idx) => (
                                                    <Box key={`${res.id}-issue-${idx}`}>
                                                        <Typography variant="body2">{issue.code ? `${issue.code}: ` : ''}{issue.message ?? 'No message'}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        ) : null}

                                        {/* Error message if present */}
                                        {res.errorMessage ? (
                                            <Typography variant="body2" color="error">{res.errorMessage}</Typography>
                                        ) : null}

                                        {/* Item title shown below issues as requested */}
                                        <Stack direction="row" spacing={1} useFlexGap alignItems="center">
                                            <Typography variant="subtitle2">{res.title}</Typography>
                                            {res.success ? <Chip label="Validated" color="success" size="small" /> : <Chip label="Failed" color="warning" size="small" />}
                                        </Stack>

                                        {/* Show raw JSON response if present */}
                                        {res.rawResponse ? (
                                            <Box sx={{ mt: 1, bgcolor: 'background.paper', p: 1, borderRadius: 1, overflow: 'auto' }}>
                                                <Typography component="pre" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                                    {JSON.stringify(res.rawResponse, null, 2)}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkActionResults(null)} variant="outlined">
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (!bulkActionResults) return;
                            const lines: string[] = [];
                            lines.push('Mock Test Bulk Action Report');
                            lines.push(`Generated: ${new Date().toISOString()}`);
                            lines.push('');
                            for (const r of bulkActionResults) {
                                lines.push(`ID: ${r.id}`);
                                lines.push(`Title: ${r.title}`);
                                lines.push(`Result: ${r.success ? 'Validated' : 'Failed'}`);
                                if (r.errorMessage) {
                                    lines.push(`Error: ${r.errorMessage}`);
                                }
                                if (r.issues && r.issues.length > 0) {
                                    lines.push('Issues:');
                                    for (const iss of r.issues) {
                                        lines.push(` - ${iss.code ?? 'Issue'}: ${iss.message ?? 'No message'}`);
                                    }
                                }
                                if (r.rawResponse) {
                                    lines.push('RawResponse:');
                                    lines.push(JSON.stringify(r.rawResponse, null, 2));
                                }
                                lines.push('');
                            }

                            const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const ts = new Date().toISOString().replace(/[:.]/g, '-');
                            a.download = `mock-test-validation-report-${ts}.txt`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                        }}
                    >
                        Download Report
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
